import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Divider,
  Space,
  Layout,
  Row,
  Col,
  Image
} from 'antd'
import { AppCard } from '@/shared/components/ui'
import {
  UserOutlined,
  LockOutlined,
  LoginOutlined,
  UserAddOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../../stores/authStore'
import type { LoginFormData } from '../../types/auth.types'
import { useTranslation, Trans } from 'react-i18next'

const { Title, Text, Paragraph } = Typography
const { Content } = Layout

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation('pages')

  const { login, isLoading: authLoading } = useAuthStore()

  // 将后端错误映射为 i18n 消息
  const mapLoginError = (err: unknown): string => {
    if (!(err instanceof Error)) return t('login.loginError')
    const msg = err.message.toLowerCase()
    if (msg.includes('用户名或密码') || msg.includes('unauthorized') || msg.includes('invalid')) {
      return t('login.invalidCredentials')
    }
    if (msg.includes('禁用') || msg.includes('disabled')) {
      return t('login.accountDisabled')
    }
    if (msg.includes('过多') || msg.includes('too many')) {
      return t('login.tooManyAttempts')
    }
    return t('login.loginError')
  }

  // 处理登录提交
  const handleSubmit = async (values: LoginFormData) => {
    setLoading(true)
    setError(null)

    try {
      await login(values)
      navigate('/dashboard')
    } catch (err) {
      setError(mapLoginError(err))
    } finally {
      setLoading(false)
    }
  }

  // 处理表单提交失败
  const handleFinishFailed = (errorInfo: any) => {
  }

  // 跳转到注册页面
  const handleGoToRegister = () => {
    navigate('/register')
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '50px 20px' }}>
        <Row justify="center" align="middle" style={{ minHeight: 'calc(100vh - 100px)' }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={8}>
            <AppCard
              borderless
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
              }}
            >
              {/* 登录标题 */}
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Image
                    src="/static/logo.svg"
                    alt="Logo"
                    preview={false}
                    width={64}
                    height={64}
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
                  <LoginOutlined /> {t('login.title')}
                </Title>
                <Paragraph type="secondary">
                  {t('login.subtitle')}
                </Paragraph>
              </div>

              {/* 错误提示 */}
              {error && (
                <Alert
                  message={t('login.loginFailed')}
                  description={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError(null)}
                  style={{ marginBottom: '24px' }}
                />
              )}

              {/* 登录表单 */}
              <Form
                form={form}
                name="login"
                layout="vertical"
                requiredMark="optional"
                onFinish={handleSubmit}
                onFinishFailed={handleFinishFailed}
                autoComplete="off"
              >
                {/* 用户名输入 */}
                <Form.Item
                  name="username"
                  label={t('login.username')}
                  rules={[
                    { required: true, message: t('login.usernameRequired') },
                    { min: 3, message: t('login.usernameMinLength') },
                    { max: 50, message: t('login.usernameMaxLength') }
                  ]}
                >
                  <Input
                    size="large"
                    prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                    placeholder={t('login.usernamePlaceholder')}
                    disabled={loading || authLoading}
                  />
                </Form.Item>

                {/* 密码输入 */}
                <Form.Item
                  name="password"
                  label={t('login.password')}
                  rules={[
                    { required: true, message: t('login.passwordRequired') },
                    { min: 6, message: t('login.passwordMinLength') }
                  ]}
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                    placeholder={t('login.passwordPlaceholder')}
                    disabled={loading || authLoading}
                  />
                </Form.Item>

                {/* 忘记密码链接 */}
                <div style={{ textAlign: 'right', marginBottom: '24px' }}>
                  <Link to="/forgot-password" style={{ color: '#1890ff' }}>
                    <Text type="secondary">{t('login.forgotPassword')}</Text>
                  </Link>
                </div>

                {/* 登录按钮 */}
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={loading || authLoading}
                    icon={<LoginOutlined />}
                    style={{
                      height: '48px',
                      fontSize: '16px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none'
                    }}
                  >
                    {loading || authLoading ? t('login.loggingIn') : t('login.loginButton')}
                  </Button>
                </Form.Item>
              </Form>

              <Divider>{t('login.or')}</Divider>

              {/* 注册引导 */}
              <div style={{ textAlign: 'center' }}>
                <Space direction="vertical" size="middle">
                  <Text type="secondary">{t('login.noAccount')}</Text>
                  <Button
                    type="default"
                    size="large"
                    icon={<UserAddOutlined />}
                    onClick={handleGoToRegister}
                    style={{
                      borderRadius: '8px',
                      borderColor: '#1890ff',
                      color: '#1890ff'
                    }}
                  >
                    {t('login.registerButton')}
                  </Button>
                </Space>
              </div>

              {/* 服务条款和隐私政策 */}
              <div style={{ marginTop: '32px', textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <Trans
                    i18nKey="login.terms"
                    ns="pages"
                    components={{
                      termsLink: <Link to="/terms" style={{ color: '#1890ff' }}>{t('login.termsLink')}</Link>,
                      privacyLink: <Link to="/privacy" style={{ color: '#1890ff' }}>{t('login.privacyLink')}</Link>
                    }}
                  />
                </Text>
              </div>
            </AppCard>

            {/* 系统信息 */}
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Text type="secondary">
                {t('login.copyright')}
              </Text>
              <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('login.support')}
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Content>
    </Layout>
  )
}

export default LoginPage